import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import { SubscriptionPlanDetail } from "../../models/subscriptionplan";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { SubscriptionplanService } from "../../services/subscriptionplan.service";
import { ToastsManager } from "ng2-toastr";
import { GroupService } from "../../services/group.service";
import { Group } from "../../models/group";
import { BikeType } from "../../models/bike-type.enum";
import { TranslateService } from "@ngx-translate/core";
import {
  TripPeriodPrice,
  TripPeriodPriceForm,
} from "../../models/trip-period-price";
import {
  DurationUnitOptions,
  DurationUnit,
} from "../../models/duration-unit.enum";
import { FormBuilder, FormArray, FormGroup } from "@angular/forms";
import { AccountService } from "../../services/account.service";

@Component({
  selector: "app-subscriptionplans-form",
  templateUrl: "./subscriptionplans-form.component.html",
  styleUrls: ["./subscriptionplans-form.component.css"],
})
export class SubscriptionplansFormComponent implements OnInit {
  tripPeriodPricesForm = this.formBuilder.group({
    tripPeriodPrices: this.formBuilder.array([]),
  });

  sp: SubscriptionPlanDetail;
  creationMode: boolean;

  startTimeH: number;
  startTimeM: number;

  endTimeH: number;
  endTimeM: number;

  tripPeriodPrices: TripPeriodPriceForm[];

  visibleGroups: Group[];
  spTypes = new Map<string, boolean>(); // map of bikes types as string
  allChecked = false;

  createForAllGroups: boolean;
  selectedGroupId: any;
  durationInput = 0;

  durationUnitOptions = DurationUnitOptions;

  @Output() updateValidation = new EventEmitter<SubscriptionPlanDetail>();
  @Output() createValidation = new EventEmitter<boolean>();

  constructor(
    public activeModal: NgbActiveModal,
    private toast: ToastsManager,
    private formBuilder: FormBuilder,
    public spService: SubscriptionplanService,
    public groupService: GroupService,
    public translate: TranslateService,
    private accountService: AccountService
  ) {}

  get tripPeriodPricesFormArray() {
    return this.tripPeriodPricesForm.get("tripPeriodPrices") as FormArray;
  }

  get isSuperAdmin() {
    return this.accountService.isSuperAdmin();
  }

  ngOnInit() {
    this.groupService.loadVisibleGroups().subscribe((groups) => {
      this.visibleGroups = groups;
    });
    if (Array.isArray(this.sp.tripPeriodPrices)) {
      this.tripPeriodPrices = this.sp.tripPeriodPrices.map(
        (trip) => new TripPeriodPriceForm(trip)
      );
    } else {
      this.sp.tripPeriodPrices = [];
    }
    if (this.creationMode) {
      this.sp = new SubscriptionPlanDetail();
      this.sp.group = new Group();
      this.sp.durationUnit = DurationUnit.YEAR;
      this.onGroupSelectChange("ALL");
    } else {
      // update mode
      this.sp.priceCents = this.sp.priceCents / 100;
      this.sp.depositCents = this.sp.depositCents / 100;
      this.sp.tripInitPriceCents = this.sp.tripInitPriceCents / 100;
      this.sp.authorizedBikes.forEach((s) => this.spTypes.set(s, true));
      this.selectedGroupId = this.sp.group.id;
      let splitted = this.sp.startTimeSlot.split("-");
      this.startTimeH = parseInt(splitted[0], 10);
      this.startTimeM = parseInt(splitted[1], 10);
      splitted = this.sp.endTimeSlot.split("-");
      this.endTimeH = parseInt(splitted[0], 10);
      this.endTimeM = parseInt(splitted[1], 10);
      this.sp.durationUnit = /[a-zA-Z]+/g.exec(
        this.sp.duration
      )[0] as DurationUnit;
      this.durationInput = parseInt(/(\d+)/g.exec(this.sp.duration)[0], 10);
      this.buildTripPeriodPricesForm();
    }
  }

  formatHourAndMinute(input: number): string {
    if (input == null) {
      return "00";
    }
    return (input < 10 ? "0" : "") + input;
  }

  onBikeTypeCheck(value: string) {
    if (this.spTypes.has(value)) {
      this.spTypes.delete(value);
      this.allChecked = false;
    } else {
      this.spTypes.set(value, true);
    }
  }

  onCheckAllClick() {
    this.allChecked = true;
    for (const item in BikeType) {
      if (isNaN(Number(item))) {
        this.spTypes.set(item, true);
      }
    }
  }

  onGroupSelectChange(value: string) {
    this.selectedGroupId = value;
    if (value === "ALL") {
      this.createForAllGroups = true;
      this.sp.group = undefined;
    } else {
      //
      this.sp.group = this.visibleGroups.find(
        (g) => g.id === parseInt(value, 10)
      );
      this.createForAllGroups = false;
    }
  }

  onSaveClick() {
    // check trip period prices
    this.sp.tripPeriodPrices = this.tripPeriodPricesFormArray
      .getRawValue()
      .map((trip: TripPeriodPriceForm) => {
        trip.priceCents = trip.priceCents * 100;
        return new TripPeriodPrice().serealize(trip);
      });
    this.sp.priceCents = this.sp.priceCents * 100;
    this.sp.depositCents = this.sp.depositCents * 100;
    this.sp.tripInitPriceCents = this.sp.tripInitPriceCents;
    if (!this.isTripPeriodPricesJoin(this.sp.tripPeriodPrices)) {
      this.toast.error(
        this.translate.instant("toast.tripnotjoin"),
        this.translate.instant("toast.error")
      );
      return;
    }

    // retrieving authorized bikes
    this.sp.authorizedBikes = Array.from(this.spTypes.keys());
    // retrieving time
    this.sp.startTimeSlot =
      this.formatHourAndMinute(this.startTimeH) +
      "-" +
      this.formatHourAndMinute(this.startTimeM);
    this.sp.endTimeSlot =
      this.formatHourAndMinute(this.endTimeH) +
      "-" +
      this.formatHourAndMinute(this.endTimeM);
    this.sp.duration = `${this.durationInput} ${this.sp.durationUnit}`;
    if (this.creationMode) {
      this.spService.post(this.sp, this.createForAllGroups).subscribe((s) => {
        this.toast.success(
          this.translate.instant("toast.spcreated"),
          this.translate.instant("toast.success")
        );
        this.createValidation.emit(true);
        this.activeModal.close();
      });
    } else {
      this.spService.put(this.sp).subscribe((s) => {
        this.toast.success(
          this.translate.instant("toast.spupdated"),
          this.translate.instant("toast.success")
        );
        this.updateValidation.emit(s);
        this.updateValidation.emit(s);
        this.activeModal.close();
      });
    }
  }

  handleAddTripPeriodPrice(): void {
    if (!this.tripPeriodPricesFormArray.length) {
      this.tripPeriodPricesFormArray.push(this.createTripPeriodPriceForm());
    } else {
      const last = this.tripPeriodPricesFormArray.at(
        this.tripPeriodPricesFormArray.length - 1
      ).value;
      this.tripPeriodPricesFormArray.push(
        this.createTripPeriodPriceForm({
          startTimeH: last.endTimeH,
          startTimeM: last.endTimeM,
          endTimeH: last.endTimeH,
          endTimeM: last.endTimeM,
          priceCents: 0,
          timeUnit: 0,
        } as TripPeriodPriceForm)
      );
    }
  }

  handleRemoveTripPeriodPrice(index: number): void {
    this.tripPeriodPricesFormArray.removeAt(index);
  }

  private createTripPeriodPriceForm(
    tripPeriodPrice?: TripPeriodPriceForm
  ): FormGroup {
    if (tripPeriodPrice) {
      return this.formBuilder.group({
        startTimeH: this.formBuilder.control({
          value: tripPeriodPrice.startTimeH,
          disabled: !this.isSuperAdmin,
        }),
        startTimeM: this.formBuilder.control({
          value: tripPeriodPrice.startTimeM,
          disabled: !this.isSuperAdmin,
        }),
        endTimeH: this.formBuilder.control({
          value: tripPeriodPrice.endTimeH,
          disabled: !this.isSuperAdmin,
        }),
        endTimeM: this.formBuilder.control({
          value: tripPeriodPrice.endTimeM,
          disabled: !this.isSuperAdmin,
        }),
        price: this.formBuilder.control({
          value: tripPeriodPrice.priceCents / 100,
          disabled: !this.isSuperAdmin,
        }),
        timeUnit: this.formBuilder.control({
          value: tripPeriodPrice.timeUnit,
          disabled: !this.isSuperAdmin,
        }),
      });
    } else {
      return this.formBuilder.group({
        startTimeH: this.formBuilder.control({ value: 0, disabled: true }),
        startTimeM: this.formBuilder.control({ value: 0, disabled: true }),
        endTimeH: this.formBuilder.control(0),
        endTimeM: this.formBuilder.control(0),
        price: this.formBuilder.control(0),
        timeUnit: this.formBuilder.control(0),
      });
    }
  }

  private buildTripPeriodPricesForm(): void {
    this.tripPeriodPrices.forEach((trip) =>
      this.tripPeriodPricesFormArray.push(this.createTripPeriodPriceForm(trip))
    );
  }

  private isTripPeriodPricesJoin(tripPeriodPrices: TripPeriodPrice[]): boolean {
    let previous: TripPeriodPrice;
    let isJoin = true;
    tripPeriodPrices.forEach((trip: TripPeriodPrice, index: number) => {
      if (!index) {
        previous = trip;
      } else {
        if (trip.startTime !== previous.endTime) {
          isJoin = false;
        }
      }
    });
    return isJoin;
  }
}



// WEBPACK FOOTER //
// ./src/app/subscriptionplans/subscriptionplans-form/subscriptionplans-form.component.ts